import React from "react";
import { Users, Activity, Database, ShieldCheck, TrendingUp, Mail, ListChecks } from "lucide-react";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-sm p-5 fade-up">
    <Icon className="w-5 h-5 mb-3" style={{ color: accent }} />
    <div className="mono text-3xl font-semibold" style={{ color: accent }}>{value}</div>
    <div className="text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mt-2">{label}</div>
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-sm p-6 fade-up">
    <h3 className="text-lg font-semibold mb-3 text-[#0B2830]">{title}</h3>
    <div className="text-sm text-[#47616B] leading-relaxed space-y-2">{children}</div>
  </div>
);

const KPIS = [
  ["Score", "Composite 0\u2013100 rank weighting EBITDA YoY growth (30%), low P/E vs peers (20%), buyback yield (20%), EBITDA margin (15%) and low payout (15%). Higher = better fit to the strategy."],
  ["Mkt Cap", "Total equity market value in USD (shares outstanding \u00D7 share price)."],
  ["P/E", "Price-to-earnings ratio: share price \u00F7 trailing 12-month earnings per share."],
  ["Peer Med P/E", "Median P/E of all screened companies in the same sector \u2014 the benchmark for 'low P/E vs peers'."],
  ["EBITDA TTM", "Trailing-twelve-month EBITDA (sum of the last 4 reported quarters), converted to USD."],
  ["Margin", "EBITDA margin = TTM EBITDA \u00F7 TTM revenue \u00D7 100 (both in the company's own currency, so it's currency-neutral)."],
  ["EBITDA YoY", "Latest quarter's EBITDA vs the same quarter one year earlier (4 quarters back), as a %."],
  ["18M Growth", "Latest quarter's EBITDA vs the quarter ~18 months (6 quarters) earlier, as a %."],
  ["Payout", "Dividend payout ratio = TTM common dividends paid \u00F7 TTM net income \u00D7 100. Shows 0% for non-dividend payers."],
  ["Buyback 3M", "Share repurchases in the most recent quarter (absolute value from the cash-flow statement), in USD."],
  ["Buyback 12M", "Share repurchases over the last 4 quarters (TTM), in USD."],
  ["BB Yield %", "Trailing-12-month buybacks \u00F7 market cap \u00D7 100 \u2014 the % of company value returned via repurchases."],
  ["18M Trend", "Sparkline of the last 6 quarterly EBITDA readings (teal if the latest reading is at/above the oldest)."],
  ["Peers (BEATS)", "Flagged when the company has a below-sector-median P/E AND above-sector-median EBITDA YoY growth."],
  ["Qualifies (\u2713)", "The check mark next to a ticker means it meets every screening criterion, not just ranks well."],
];

export default function ScreenerInfo({ stats }) {
  return (
    <div className="mt-10 space-y-6" data-testid="screener-info">
      <div className="flex items-center gap-2 text-[#0284C7]">
        <TrendingUp className="w-5 h-5" />
        <span className="mono text-xs uppercase tracking-[0.25em]">About this screener</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat icon={Users} label="Unique visitors" accent="#0284C7"
          value={stats ? stats.unique_visitors.toLocaleString() : "\u2014"} />
        <Stat icon={Activity} label="Total page views" accent="#0D9488"
          value={stats ? stats.total_visits.toLocaleString() : "\u2014"} />
        <Stat icon={Database} label="Companies screened" accent="#D97706" value="59" />
      </div>

      {/* KPIs subsection */}
      <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-sm p-6 fade-up" data-testid="kpi-definitions">
        <div className="flex items-center gap-2 mb-4">
          <ListChecks className="w-4 h-4 text-[#0284C7]" />
          <h3 className="text-lg font-semibold text-[#0B2830]">KPIs</h3>
          <span className="text-xs text-[#7C949C]">&mdash; what each column in the table means</span>
        </div>
        <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3">
          {KPIS.map(([term, def]) => (
            <div key={term} className="flex flex-col border-b border-[#E6EEF1] pb-3" data-testid={`kpi-${term}`}>
              <dt className="mono text-sm font-semibold text-[#0B2830]">{term}</dt>
              <dd className="text-sm text-[#47616B] leading-relaxed mt-0.5">{def}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card title="Screening methodology">
          <p>Each company is scored and gated against these criteria:</p>
          <ul className="list-disc pl-5 space-y-1 text-[#47616B]">
            <li>Market cap &ge; $10B</li>
            <li>EBITDA YoY growth &ge; 5% and positive 18-month EBITDA growth</li>
            <li>Dividend payout ratio &le; 35% (low payout)</li>
            <li>P/E below its own sector median (low P/E vs peers)</li>
            <li>High EBITDA (&ge; $1B TTM)</li>
            <li>"Beats peers" = below-median P/E and above-median EBITDA growth within sector</li>
          </ul>
          <p>The composite score weights EBITDA growth, low P/E, buyback yield, EBITDA margin, and low payout.</p>
        </Card>

        <Card title="Data & coverage">
          <p>Fundamentals are sourced from free public data (stockanalysis.com); 2-year price history from Yahoo Finance's public chart API.</p>
          <p>Foreign-currency financials (EBITDA, buybacks, dividends) are converted to USD at current FX rates for cross-company comparability.</p>
          <p>The universe spans global large-caps (US-listed and ADRs) across technology, healthcare, energy, consumer, financials, industrials, and communications.</p>
        </Card>

        <Card title="Daily email report">
          <p className="flex items-start gap-2">
            <Mail className="w-4 h-4 text-[#0284C7] mt-0.5 shrink-0" />
            The list auto-refreshes and is emailed to subscribers every day at 08:00 UTC, with the
            fully-qualified names and the full top-20 ranked table. Manage recipients from the
            "Daily Email" button above.
          </p>
        </Card>

        <Card title="Disclaimer">
          <p className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-[#D97706] mt-0.5 shrink-0" />
            This tool is for informational and educational purposes only and does not constitute
            investment advice, a recommendation, or a solicitation to buy or sell any security.
            Data may be delayed or contain errors. Always do your own research.
          </p>
        </Card>
      </div>
    </div>
  );
}
